import classnames from 'classnames';
import { produce } from 'immer';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router';

import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import { HeaderEntry } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { Config } from 'src/lib/config/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { PublicVariantWarning } from '../PublicVariantWarning';

import { HeaderEditor } from './headerEditor/HeaderEditor';

export const HeadersEditor = ({
  className,
  headerDefinitions,
  setHeaderDefinitions,
  sharedHeaders,
  setSharedHeaders,
  withCheckbox,
  sharedHeadersDisabled,
  sharedHeadersDisabledTooltipContent,
  headersDisabled,
  headersDisabledTooltipContent,
  settingsEndpointUrl,
  settingsSubscriptionUrl,
  explorerBehavioralFlags,
}: {
  className?: string;
  headerDefinitions: HeaderEntry[];
  setHeaderDefinitions: (
    nextValue: React.SetStateAction<HeaderEntry[]>,
  ) => void;
  sharedHeaders?: HeaderEntry[];
  setSharedHeaders?: React.Dispatch<React.SetStateAction<HeaderEntry[]>>;
  withCheckbox: boolean;
  sharedHeadersDisabled: boolean;
  sharedHeadersDisabledTooltipContent?: string | undefined;
  headersDisabled: boolean;
  headersDisabledTooltipContent?: string | undefined;
  settingsEndpointUrl?: string | null;
  settingsSubscriptionUrl?: string | null;
  explorerBehavioralFlags?: ExplorerBehavioralFlags;
}) => {
  // INFO: If header is currently enabled, uncheck any headers currently
  // checked headers with the same headerName
  const uncheckSimilarHeaders = React.useCallback(
    (headerKey: number, headerName: string): void => {
      const uncheckInDefinitions = (currentDefinitions: HeaderEntry[]) =>
        produce(currentDefinitions, (draftHeaders) => {
          draftHeaders.forEach((header) => {
            if (header.headerName === headerName && header.key !== headerKey) {
              // eslint-disable-next-line no-param-reassign
              header.checked = false;
            }
          });
        });
      setHeaderDefinitions(uncheckInDefinitions);
      setSharedHeaders?.(uncheckInDefinitions);
    },
    [setHeaderDefinitions, setSharedHeaders],
  );

  const handleSharedHeaderCheck = useCallback(
    (newCheckedValue: boolean, item: HeaderEntry) => {
      setSharedHeaders?.((currSharedHeaders) =>
        produce(currSharedHeaders, (draftSharedHeaders) => {
          const draftSharedHeader = draftSharedHeaders.find(
            ({ key }) => item.key === key,
          );
          if (draftSharedHeader) {
            draftSharedHeader.checked = newCheckedValue;
          }
        }),
      );
      if (newCheckedValue) {
        uncheckSimilarHeaders(item.key, item.headerName);
      }
    },
    [setSharedHeaders, uncheckSimilarHeaders],
  );

  const handleHeaderCheck = useCallback(
    (newCheckedValue: boolean, item: HeaderEntry) => {
      setHeaderDefinitions((currentHeaderDefinitions) =>
        produce(currentHeaderDefinitions, (draftHeaders) => {
          const draftHeader = draftHeaders.find(({ key }) => item.key === key);
          if (draftHeader) {
            draftHeader.checked = newCheckedValue;
          }
        }),
      );
      if (newCheckedValue) {
        uncheckSimilarHeaders(item.key, item.headerName);
      }
    },
    [setHeaderDefinitions, uncheckSimilarHeaders],
  );

  const handleHeaderNameChange = useCallback(
    (newHeaderName: string, item: HeaderEntry) => {
      setHeaderDefinitions((currentHeaderDefinitions) =>
        produce(currentHeaderDefinitions, (draftHeaders) => {
          const draftHeader = draftHeaders.find(({ key }) => item.key === key);
          if (draftHeader) {
            draftHeader.headerName = newHeaderName;
          }
        }),
      );
      if (item.checked) {
        uncheckSimilarHeaders(item.key, newHeaderName);
      }
    },
    [setHeaderDefinitions, uncheckSimilarHeaders],
  );

  const handleHeaderValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, item: HeaderEntry) => {
      const value = event.target.value;
      setHeaderDefinitions((currentHeaderDefinitions) =>
        produce(currentHeaderDefinitions, (draftHeaders) => {
          const draftHeader = draftHeaders.find(({ key }) => item.key === key);
          if (!draftHeader) return;
          draftHeader.value = value;
        }),
      );
    },
    [setHeaderDefinitions],
  );

  const handleHeaderDeletion = useCallback(
    (item: HeaderEntry) =>
      setHeaderDefinitions((currentHeaderDefinitions) =>
        produce(currentHeaderDefinitions, (draftHeaders) => {
          const draftHeaderIndex = draftHeaders.findIndex(
            ({ key }) => item.key === key,
          );
          const draftHeader = draftHeaders[draftHeaderIndex];
          if (!draftHeader) return;

          draftHeaders.splice(draftHeaderIndex, 1);
        }),
      ),
    [setHeaderDefinitions],
  );

  const location = useLocation();

  return (
    <div
      // Exclude the response and response headers from fullstory
      className={classnames('fs-exclude space-y-2 overflow-auto', className)}
    >
      <PublicVariantWarning
        settingsEndpointUrl={settingsEndpointUrl}
        settingsSubscriptionUrl={settingsSubscriptionUrl}
        explorerBehavioralFlags={explorerBehavioralFlags}
      />
      {sharedHeaders?.map((item) => {
        return (
          <HeaderEditor
            key={item.key}
            item={item}
            isDisabled={sharedHeadersDisabled}
            withCheckbox={withCheckbox}
            disabledLink={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.connectionSettings,
            })}
            disabledTooltipContent={sharedHeadersDisabledTooltipContent}
            onCheck={(value) => handleSharedHeaderCheck(value, item)}
            isDeleteDisabled={
              sharedHeaders?.length === 1 && !item.headerName && !item.value
            }
          />
        );
      })}
      {headerDefinitions.map((item) => (
        <HeaderEditor
          key={item.key}
          item={item}
          isDisabled={headersDisabled}
          disabledLink={setOverlayVisibleLocation({
            location,
            overlayName: Config.modals.connectionSettings,
          })}
          disabledTooltipContent={headersDisabledTooltipContent}
          withCheckbox={withCheckbox}
          onCheck={(value) => handleHeaderCheck(value, item)}
          onHeaderNameChanged={(value) => handleHeaderNameChange(value, item)}
          onHeaderValueChanged={(event) => handleHeaderValueChange(event, item)}
          onTrashClick={() => handleHeaderDeletion(item)}
          isDeleteDisabled={
            headerDefinitions.length === 1 && !item.headerName && !item.value
          }
        />
      ))}
    </div>
  );
};
